import * as React from 'react';
import { Check, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { BottomSheet, BottomSheetContent } from './BottomSheet';
import { Button } from './Button';
import { Form, FormField, useZodForm } from './Form';
import { Textarea } from './Input';

export const noteSchema = z.object({
  text: z.string().trim().min(1, 'Write the note first'),
});

export type NoteValues = z.infer<typeof noteSchema>;

export interface NoteComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the trimmed note. The draft clears once this resolves;
   * return `false` (or reject) to keep it, for a save that failed. Closing
   * the sheet is the screen's call.
   */
  onSubmit: (text: string) => void | boolean | Promise<void | boolean>;
  /** A save is running: the confirm spins and will not press again. */
  saving?: boolean;
  /** The sheet's heading: "Create note", "Edit note". */
  title?: string;
  placeholder?: string;
  /**
   * What the box holds each time the sheet opens: the note being changed.
   * Left out, it opens empty, for a new one.
   */
  defaultText?: string;
  /**
   * Puts Delete note along the foot, which calls this: for the viewer's own
   * note opened to change it. The screen asks before it deletes.
   */
  onDelete?: () => void;
  /** The delete is running: its button spins and will not press again. */
  deleting?: boolean;
}

/**
 * Writing a note on a phone: a sheet with one box, the tick top right to
 * save it. Given `defaultText` and `onDelete` it is the same sheet for
 * changing the viewer's own note, with Delete note along the foot.
 *
 *   <NoteComposer open={open} onOpenChange={setOpen}
 *     onSubmit={async (text) => { await addNote(text); setOpen(false); }}
 *     saving={isPending} />
 *
 * A sheet rather than a box under the notes, because on a phone a box under
 * a list is under the keyboard. The draft is the composer's own; the screen
 * only sees the finished note.
 */
export function NoteComposer({
  open,
  onOpenChange,
  onSubmit,
  saving = false,
  title = 'Create note',
  placeholder = 'Type your note',
  defaultText = '',
  onDelete,
  deleting = false,
}: NoteComposerProps) {
  const formId = React.useId();
  const form = useZodForm(noteSchema, { defaultValues: { text: defaultText } });

  // An edit opens on the note it changes each time, not on an abandoned
  // change. A new note's draft is kept across closing, as it always was.
  React.useEffect(() => {
    if (open && defaultText) form.reset({ text: defaultText });
  }, [open, defaultText, form]);

  const submit = async ({ text }: NoteValues) => {
    try {
      const kept = await onSubmit(text);
      if (kept !== false) form.reset({ text: '' });
    } catch {
      // The screen reported the failure; the draft stays for another go.
    }
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent
        title={title}
        size="full"
        action={
          <Button
            type="submit"
            form={formId}
            variant="soft"
            size="icon-lg"
            pill
            loading={saving}
            aria-label="Save note"
          >
            <Check />
          </Button>
        }
        footer={
          onDelete ? (
            <Button
              variant="destructive-secondary"
              size="xl"
              pill
              leadingIcon={<Trash2 />}
              loading={deleting}
              onClick={onDelete}
            >
              Delete note
            </Button>
          ) : undefined
        }
      >
        <Form form={form} id={formId} onSubmit={submit}>
          <FormField form={form} name="text" label="Note">
            {(field) => (
              <Textarea {...field} rows={6} placeholder={placeholder} />
            )}
          </FormField>
        </Form>
      </BottomSheetContent>
    </BottomSheet>
  );
}
